import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workflowBatchApi } from '@/lib/api/workflow-batch.api';

const QUERY_KEY = ['workflow-batch-runs'];

export const useWorkflowBatchRuns = (workflowId: string) =>
  useQuery({
    queryKey: [...QUERY_KEY, { workflowId }],
    queryFn: () => workflowBatchApi.list({ workflow_id: workflowId }),
    refetchInterval: 5000,
  });

export const useWorkflowBatchRun = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => workflowBatchApi.get(id!),
    enabled: !!id,
    refetchInterval: 3000,
  });

export const useWorkflowBatchRunRows = (
  id: string | undefined,
  params: { page?: number; limit?: number; status?: string },
) =>
  useQuery({
    queryKey: [...QUERY_KEY, id, 'rows', params],
    queryFn: () => workflowBatchApi.getRows(id!, params),
    enabled: !!id,
    refetchInterval: 5000,
  });

export const useCreateBatchRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workflowBatchApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUploadBatchRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      workflowId,
      config,
    }: {
      file: File;
      workflowId: string;
      config: { name?: string; delay_ms?: number; concurrency?: number };
    }) => workflowBatchApi.upload(file, workflowId, config),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useStartBatchRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workflowBatchApi.start,
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useStopBatchRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workflowBatchApi.stop,
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteBatchRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: workflowBatchApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
